import React, { useEffect, useState } from "react";
import Select, { components } from "react-select";
import "../Style/Pages/Dashboard.css";
import { useSelector } from "react-redux";
import Dashcard from "../Component/Dashcard";
import { useSearchParams } from "react-router-dom";
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Dashgraph from "../Component/Dashgraph";
import { getWithTokenAPI } from "../Util/Api";

const Dashboard = () => {
  const auth = useSelector((state) => state.auth);
  console.log(auth, "AUTH");
  const token = auth.token;
  const permission = useSelector((state) => state?.auth?.permission);
  const [searchParams, setSearchParams] = useSearchParams();
  const districtFromUrl = searchParams.get("district") || "";
  const cityFromUrl = searchParams.get("city") || "";

  const [district, setDistrict] = useState(districtFromUrl ? { label: districtFromUrl, value: districtFromUrl } : null);
  const [city, setCity] = useState(cityFromUrl ? { label: cityFromUrl, value: cityFromUrl } : null);
  const [area, setArea] = useState(null);

  const [districtOptions, setDistrictOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [allCitiesData, setAllCitiesData] = useState([]);

  const areaOptions = [...districtOptions];

  /* ---------------- CUSTOM LABEL COMPONENTS ---------------- */

  const SingleValueWithLabel = (label) => (props) => (
    <components.SingleValue {...props}>
      <span className="filter-prefix">{label} :</span>
      <span className="filter-value">{props.data.label}</span>
    </components.SingleValue>
  );

  const PlaceholderWithLabel = (label) => (props) => (
    <components.Placeholder {...props}>
      <span className="filter-prefix">{label} :</span>
      <span className="filter-placeholder">All</span>
    </components.Placeholder>
  );

  /* ---------------- REACT SELECT STYLES ---------------- */

  const customStyles = {
    control: (base, state) => ({
      ...base,
      height: "36px",
      width: "180px", // ✅ fixed width
      // minWidth: "160px",
      // maxWidth: "0px",
      border: "0.6px solid #dcdcdc",
      borderRadius: "4px",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(74, 144, 226, 0.2)" : "none",
      borderColor: state.isFocused ? "#4a90e2" : "#dcdcdc",
      "&:hover": {
        borderColor: "#999",
      },
      backgroundColor: "white",
    }),
    option: (base, state) => {
      const isSelected = state.isSelected;
      const isFocused = state.isFocused;

      return {
        ...base,
        backgroundColor: isSelected
          ? "#013A28" // ✅ selected always wins
          : isFocused
            ? "#02543a53" // hover only for non-selected
            : "white",
        color: isSelected ? "white" : "#333",
        cursor: "pointer",

        // ❌ disable hover override on selected
        ":hover": {
          backgroundColor: isSelected ? "#013A28" : "#02543a53",
        },

        ":active": {
          backgroundColor: isSelected ? "#013A28" : "#02543a53",
        },
      };
    },

    menu: (base) => ({
      ...base,
      borderRadius: "4px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      zIndex: "99 !important",
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
      maxHeight: "180px",
      overflow: "auto",
      // Scrollbar matching your #8C1007 theme
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-track": {
        background: "#013a284a",
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#013a2840",
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: "#013a2851",
      },
      scrollbarWidth: "thin",
      scrollbarColor: "#02543a53 #b0b0b06a",
    }),
    singleValue: (base) => ({
      ...base,
      color: "rgb(99, 99, 99)", // Matching your color
      fontSize: "14px",
      fontWeight: "500",
      width: "160px",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#acacac",
      fontSize: "14px",
      fontWeight: "500",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#8c8c8c",
      padding: "2px",
      "&:hover": {
        color: "#666",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 5px",
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: 0, // remove default padding
      margin: 0, // remove margin
      cursor: "pointer",
    }),
  };

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await getWithTokenAPI(token, `/admin/city/list-all`);

        const cities = response?.data?.data || [];

        setAllCitiesData(cities);

        // 🔹 Get unique districts
        const uniqueDistricts = [
          ...new Set(cities.map((item) => item.district)),
        ];

        const formattedDistricts = uniqueDistricts.map((d) => ({
          value: d,
          label: d,
        }));

        setDistrictOptions(formattedDistricts);
      } catch (err) {
        console.log(err);
      }
    };

    if (token) {
      fetchDistricts();
    }
  }, [token]);

  useEffect(() => {
    if (district) {
      const filteredCities = allCitiesData
        .filter((item) => item.district === district.value)
        .map((item) => ({
          value: item.city,
          label: item.city,
        }));

      // remove duplicates
      const uniqueCities = [
        ...new Map(filteredCities.map((item) => [item.value, item])).values(),
      ];

      setCityOptions(uniqueCities);
      
      // If the current city in URL is not valid for the new district, reset it
      if (city && !uniqueCities.find(c => c.value === city.value)) {
        setCity(null);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("city");
        setSearchParams(newParams);
      }
    } else {
      setCityOptions([]);
      setCity(null);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("district");
      newParams.delete("city");
      setSearchParams(newParams);
    }
  }, [district, allCitiesData]);

  const handleDistrictChange = (selected) => {
    setDistrict(selected);
    const newParams = new URLSearchParams(searchParams);
    if (selected) {
      newParams.set("district", selected.value);
    } else {
      newParams.delete("district");
    }
    newParams.delete("city"); // reset city in URL when district changes
    setSearchParams(newParams);
  };

  const handleCityChange = (selected) => {
    setCity(selected);
    const newParams = new URLSearchParams(searchParams);
    if (selected) {
      newParams.set("city", selected.value);
    } else {
      newParams.delete("city");
    }
    setSearchParams(newParams);
  };

  return (
    <div className="dashboardpage">
      <div className="dashfilter">
        <span className="headline">Overview</span>

        <div className="dashfilter sub">
           {/* DISTRICT */}
          <Select
            options={districtOptions}
            value={district}
            onChange={handleDistrictChange}
            styles={customStyles}
            isSearchable={false}
            isClearable={true}
            placeholder=""
            components={{
              SingleValue: SingleValueWithLabel("District"),
              Placeholder: PlaceholderWithLabel("District"),
              IndicatorSeparator: null,
            }}
          />

          {/* CITY */}
          <Select
            options={cityOptions}
            value={city}
            onChange={handleCityChange}
            styles={customStyles}
            isClearable={true}
            isSearchable={false}
            placeholder=""
            components={{
              SingleValue: SingleValueWithLabel("City"),
              Placeholder: PlaceholderWithLabel("City"),
              IndicatorSeparator: null,
            }}
          />

          {/* AREA */}
          {/* <Select
            options={areaOptions}
            value={area}
            onChange={setArea}
            styles={customStyles}
            isClearable={true}
            isSearchable={false}
            placeholder=""
            components={{
              SingleValue: SingleValueWithLabel("Area"),
              Placeholder: PlaceholderWithLabel("Area"),
              IndicatorSeparator: null,
            }}
          /> */}

          {/* <button className="applybutton">Apply</button> */}
        </div>
      </div>
      <div className="dcard">
        <Dashcard page={"home"} city={city?.value} district={district?.value} />
      </div>
      <div className="dgraph">
        <Dashgraph />
      </div>
    </div>
  );
};

export default Dashboard;
